import React, { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Fade, Stack } from '@mui/material'
import { MegaphoneOffIcon } from 'lucide-react'

import { Button, Skeleton } from '@repo/ui'
import { NARROW_COLUMN_WIDTH } from 'config'

import { AnnouncementCard, EmptyState } from 'components/display'
import { QueryErrorState } from 'components/feedback'
import { Header } from 'components/navigation'
import { DEFERRED_LOADING_FADE_MS, useDeferredLoading } from 'hooks'
import { useAnnouncement, useMarkAnnouncementSeen } from 'hooks/api'
import { ROUTES } from 'routes'
import { isMissing } from 'services/announcementsApi'

import { AnnouncementPager } from './AnnouncementPager'
import { WhatsNewAnnouncementSkeleton } from './WhatsNewAnnouncementSkeleton'

/**
 * `/whats-new/:id` — ONE announcement, in full (VRG-221).
 *
 * ## Why a detail page exists at all
 *
 * Two problems, one page.
 *
 * **Length.** Announcement bodies are free text with no limit — staff author them in a
 * plain textarea at `/internal/announcements`. The archive used to render every body in
 * full, so a single long notice (the seeded deprecation one is already the longest
 * thing on that page by several times) pushed everything else below the fold and made
 * the feed unscannable. The feed now clamps to three lines; this is where the rest
 * lives, so nothing is lost by clamping.
 *
 * **Address.** Until this route, an announcement had no URL. "Read this one" was not a
 * thing you could send a colleague: the archive is one address and the recipient had to
 * find the right card by scrolling. For a deprecation with a deadline attached that is
 * not a convenience gap — it is the difference between a notice being actionable and
 * being merely published.
 *
 * ## It renders no content of its own
 *
 * The body, the kind chip, the "you asked for this" links and the CTA all come from
 * `AnnouncementCard` (§36) — the same component the modal and the feed use, in a third
 * variant. A page that re-rendered the announcement would be the fourth hand-roll of
 * the thing that component was created to keep from drifting, and the drift would be
 * invisible: nobody opens the modal and the detail page side by side.
 *
 * This page owns only the FRAME — the header, the wayfinding, the four states, and the
 * neighbour pager beneath the card. The pager is frame, not content: it is about where
 * this announcement sits in the archive, which is a fact about the PAGE rather than
 * about the announcement, so it would have no meaning in the modal or the feed. That is
 * the test for anything added here — if the modal would want it too, it belongs in the
 * card. See `AnnouncementPager` for what else was considered for this space and why each
 * was turned down.
 *
 * ## The states, and why "not found" is separate from "error"
 *
 * A bad id is not a failure, it is an answer, and conflating them produces a "Try
 * again" button on a URL that can never resolve. So:
 *   - loading   → footprint-matched skeleton, header included (§19)
 *   - missing   → a plain not-found with a way back to the archive. 404 AND 400: a
 *                 truncated link fails uuid parsing at the edge, and to the reader that
 *                 is the same situation as a stale id.
 *   - error     → `QueryErrorState`, as the archive uses, with a retry that means something
 *   - loaded    → the card
 *
 * Note what "missing" covers: the API returns the same 404 for deleted, still-draft, and
 * gated-away-from-this-caller. That is deliberate — a 403 would confirm the announcement
 * exists and name a feature the reader is not supposed to know about — and this page
 * cannot tell them apart even if it wanted to.
 */
export const WhatsNewAnnouncementPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: announcement, isLoading, error, refetch } = useAnnouncement(id)
  const markSeen = useMarkAnnouncementSeen()

  /**
   * Whether the skeleton may be on screen. A single announcement comes back in ~30ms, which
   * without this shows a skeleton for two frames and takes it away again. See
   * `useDeferredLoading`.
   */
  const showSkeleton = useDeferredLoading(isLoading)

  /**
   * READING IT HERE MARKS IT SEEN, matching the archive.
   *
   * Without this, opening a link to an announcement would leave the Help row's "New"
   * dot claiming it is unread — after the reader has just read the whole thing at its
   * own address, which is the most thorough way there is to read it. The archive
   * already marks on view for the same reason; a detail page that did not would make
   * the badge depend on which route you happened to use.
   *
   * `requestedRef` guards the window between the mutation firing and the feed
   * invalidation landing, where the row is still `seen: false` in cache and a re-render
   * would otherwise post it again.
   */
  const requestedRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!announcement || announcement.seen) return
    if (requestedRef.current.has(announcement.id)) return
    requestedRef.current.add(announcement.id)
    markSeen.mutate(announcement.id)
    // Keyed on the announcement alone. `markSeen` is a mutation object whose identity
    // changes as its state does, so depending on it would re-run this on every settle —
    // the loop `requestedRef` exists to stop.
  }, [announcement])

  /*
    The column cap wraps the WHOLE page, title included — the same call the archive
    makes, and for the same reason: capping only the content leaves the title at the
    page's left edge while the content sits centred hundreds of pixels away, reading as
    two unrelated things.
  */
  const wrap = (children: React.ReactNode) => (
    <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>{children}</Box>
  )

  if (showSkeleton) {
    return wrap(
      <>
        {/*
          Skeleton bars for BOTH the title and the breadcrumb, not just the title.
          `Header` renders its wayfinding row only when `breadcrumbsOveride` is truthy,
          so omitting the crumb here would drop that row entirely and put the loading
          content ~25px above where the loaded content lands (§19 — the footprint has to
          match, not merely the shape).
        */}
        <Header
          title={<Skeleton width={360} height={31} />}
          breadcrumbsOveride={<Skeleton width={90} height={20} />}
        />
        <WhatsNewAnnouncementSkeleton />
      </>,
    )
  }

  /*
    Loading, but inside the skeleton's grace period. Nothing, rather than falling through to
    the "isn't available" empty state below — which is what `error && isMissing(error)` would
    NOT catch, but `error || !announcement` would: a query still in flight has no announcement,
    so the not-found branch would fire on every load for as long as the grace period lasts.
  */
  if (isLoading) return null

  if (error && isMissing(error)) {
    return wrap(
      <>
        {/* A generic title, because there is no announcement to name. "Announcement"
            rather than "Not found" — the heading names the surface, and the empty state
            below is what says what happened. */}
        <Header title="Announcement" breadcrumbsOveride="Announcement" />
        <EmptyState
          icon={<MegaphoneOffIcon strokeWidth={1} />}
          title="This announcement isn't available"
          // Says WHY without guessing which reason applies — the API deliberately does
          // not tell us, and inventing "it was deleted" would be a confident lie
          // two-thirds of the time. No "Try again": nothing about retrying a stale link
          // will change the answer, and offering it wastes the reader's next click.
          text="The link may be out of date, or the announcement may no longer be published. Everything else is in What's new."
          buttons={
            <Button variant="secondary" onClick={() => navigate(ROUTES.WHATS_NEW)}>
              Go to What&apos;s new
            </Button>
          }
        />
      </>,
    )
  }

  if (error || !announcement) {
    return wrap(
      <>
        <Header title="Announcement" breadcrumbsOveride="Announcement" />
        {/*
          `!announcement` shares this branch with a real error deliberately. It should be
          unreachable — the query is either loading, failed, or has data — but if it ever
          is reached, a visible "couldn't load, try again" is the right failure: the
          alternative reading of `isLoading || !announcement` is a skeleton that never
          resolves, which looks like the page is still working.
        */}
        <QueryErrorState
          error={error}
          subject="this announcement"
          onRetry={() => refetch()}
        />
      </>,
    )
  }

  return wrap(
    /* Opacity only, over a footprint the skeleton already matches — polish on a stable
       layout, not a mask for movement. Wraps the header too: the skeleton replaces the title
       and breadcrumb with bars, so the whole page is what arrives at once. */
    <Fade in timeout={DEFERRED_LOADING_FADE_MS}>
      <Box>
        {/*
          The ANNOUNCEMENT'S OWN TITLE is the page title, and the breadcrumb is what says
          where you are — the same shape `SupportRequestPage` uses. That is why the card's
          `detail` variant renders no title: it would be the same words twice, 12px apart.

          `breadcrumbsOveride` replaces the LAST crumb, which is otherwise the raw uuid
          from the URL. The parent crumb comes from the path segment and reads "What's
          new" via the label map in `Breadcrumbs` — without it, `toTitleCase('whats-new')`
          yields "Whats-New".

          No explicit back-link: `ReferrerBackLink` suppresses itself when the referrer is
          the breadcrumb parent, and `/whats-new` is exactly that. The breadcrumb IS the
          way back for a hierarchical drill-in, and rendering both would be two controls
          with one meaning. The referrer the card passes still does work — `Breadcrumbs`
          swaps the parent crumb's bare route for it, so returning restores the archive's
          own URL rather than a reset one.
        */}
          <Header title={announcement.title} breadcrumbsOveride="Announcement" />
          {/* The card, then the neighbours. `spacing={6}` rather than the card's own
              internal 3: the pager is a separate region of the page, not another row of
              the announcement, and at the tighter gap it read as part of the card. */}
          <Stack spacing={6}>
            <AnnouncementCard announcement={announcement} variant="detail" />
            <AnnouncementPager currentId={announcement.id} />
          </Stack>
      </Box>
    </Fade>,
  )
}
